import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/lib/status";
import { Loader2, Boxes, TriangleAlert, Wrench, ClipboardList, ArrowRight, Plus, CalendarClock, Zap } from "lucide-react";

const StatCard = ({ label, value, sub, icon: Icon, color, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="flex flex-col items-start gap-3 rounded-2xl border border-border bg-card p-5 text-left card-hover">
    <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: color + "1A", color }}><Icon className="h-5 w-5" /></span>
    <div>
      <div className="font-mono text-3xl font-extrabold text-foreground">{value}</div>
      <div className="text-sm font-medium text-foreground/80">{label}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  </button>
);

export default function ManagementDashboard() {
  const [stats, setStats] = useState(null);
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => { api.stats().then(setStats).catch(() => setStats(false)); }, []);

  if (stats === null) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-8">
      <div className="mb-8">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Overview</span>
        <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">Welcome back, {user?.name || "technician"}</h1>
        <p className="mt-1 text-sm text-muted-foreground">Live status across assets, faults, maintenance and site surveys.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testid="stat-assets" label="Total assets" value={stats.assets_total} icon={Boxes} color="#10B981" onClick={() => nav("/assets")} />
        <StatCard testid="stat-faults" label="Open faults" value={stats.faults_open} sub={`${stats.faults_total} total`} icon={TriangleAlert} color="#EF4444" onClick={() => nav("/faults")} />
        <StatCard testid="stat-overdue" label="Overdue jobs" value={stats.maintenance_overdue} sub={`${stats.maintenance_due} due soon`} icon={Wrench} color="#F59E0B" onClick={() => nav("/maintenance")} />
        <StatCard testid="stat-surveys" label="Site surveys" value={stats.surveys_total} icon={ClipboardList} color="#3B82F6" onClick={() => nav("/surveys")} />
      </div>

      {(stats.pat_overdue > 0 || stats.pat_due > 0) && (
        <button data-testid="pat-alert" onClick={() => nav(`/assets?pat=${stats.pat_overdue > 0 ? "overdue" : "due"}`)} className="mt-4 flex w-full items-center gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-left tap-scale">
          <Zap className="h-5 w-5 shrink-0 text-amber-500" />
          <span className="text-sm text-foreground"><b>{stats.pat_overdue}</b> PAT overdue · <b>{stats.pat_due}</b> due soon — tap to review electrical tests</span>
          <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      )}

      {/* Asset status breakdown */}
      {stats.assets_total > 0 && (
        <div className="mt-6 rounded-2xl border border-border bg-card p-5">
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Assets by status</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.assets_by_status).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 rounded-lg border border-border bg-secondary/30 px-3 py-1.5">
                <StatusBadge value={k} />
                <span className="font-mono text-sm font-bold text-foreground">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent faults */}
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Recent faults</h3>
            <button onClick={() => nav("/faults")} className="flex items-center gap-1 text-xs text-primary tap-scale">View all <ArrowRight className="h-3 w-3" /></button>
          </div>
          {stats.recent_faults.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No faults reported. 🎉</p>
          ) : (
            <div className="space-y-2">
              {stats.recent_faults.map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-3 rounded-lg border border-border/60 px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{f.title}</div>
                    <div className="truncate text-xs text-muted-foreground">{f.asset_name || f.area || "General"}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <StatusBadge value={f.priority} />
                    <StatusBadge value={f.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent surveys */}
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Recent surveys</h3>
            <button onClick={() => nav("/surveys")} className="flex items-center gap-1 text-xs text-primary tap-scale">View all <ArrowRight className="h-3 w-3" /></button>
          </div>
          {stats.recent_surveys.length === 0 ? (
            <div className="py-6 text-center">
              <p className="text-sm text-muted-foreground">No surveys yet.</p>
              <button onClick={() => nav("/surveys")} className="mt-2 inline-flex items-center gap-1 text-xs text-primary"><Plus className="h-3 w-3" /> Start a survey</button>
            </div>
          ) : (
            <div className="space-y-2">
              {stats.recent_surveys.map((s) => (
                <button key={s.id} onClick={() => nav(`/survey/${s.id}`)} className="flex w-full items-center justify-between gap-3 rounded-lg border border-border/60 px-3 py-2.5 text-left tap-scale hover:border-primary/40">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{s.title}</div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground"><CalendarClock className="h-3 w-3" /> {new Date(s.updated_at).toLocaleDateString()}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
